import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { ACTION_LABEL, SOURCE_LABEL, BRAND, batchLabel, errorMessage, esc, formatDateTime, handleError, toast, useBodyScrollLock } from "./shared";

// Rincian paket satu student: status tiap barang, ubah status, catatan,
// riwayat, dan QR Code. Dibuka dari daftar Distribution atau dari hasil scan.
const StudentPackageDetail = ({ studentRowId, initial = null, fromScan = false, onClose, onChanged, onScanNext }) => {
  useBodyScrollLock();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(initial);
  const [loading, setLoading] = useState(!initial);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("items");
  const [history, setHistory] = useState(null);
  // Tambah barang: panel pilihan barang aktif (daftar induk dari server).
  const [adding, setAdding] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [pick, setPick] = useState([]);
  // Asal aksi, dicatat di riwayat.
  const source = fromScan ? "qr_scanner" : "detail_page";

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`/student-package/students/${studentRowId}`);
      setDetail(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return handleError(err, navigate);
      setLoadError(errorMessage(err, "Unable to load this student's package."));
    } finally {
      setLoading(false);
    }
  }, [studentRowId, navigate]);

  const loadHistory = useCallback(async () => {
    setHistory(null);
    try {
      const res = await axios.get(`/student-package/students/${studentRowId}/history`);
      setHistory(res.data.history);
    } catch (err) {
      setHistory([]);
      handleError(err, navigate, "Unable to load the history.");
    }
  }, [studentRowId, navigate]);

  useEffect(() => {
    if (!initial) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentRowId]);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && !Swal.isVisible()) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const applyResult = (data) => {
    if (data && data.detail) setDetail(data.detail);
    setSelected([]);
    if (tab === "history") loadHistory();
    onChanged && onChanged();
  };

  const items = detail ? detail.items : [];
  const pendingItems = items.filter((i) => i.status === "pending");
  const receivedCount = items.length - pendingItems.length;
  const student = detail ? detail.student : null;

  const markReceived = async (targets) => {
    if (!targets.length) return;
    const list = targets.map((i) => `<li>${esc(i.itemName)}</li>`).join("");
    const result = await Swal.fire({
      title: "Mark as received?",
      html: `<p style="margin:0 0 8px">${esc(student.name)} receives:</p><ul style="text-align:left;margin:0 auto;display:inline-block">${list}</ul>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Mark ${targets.length} as received`,
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
      const res = await axios.post(`/student-package/students/${studentRowId}/status`, {
        updates: targets.map((i) => ({ distributionId: i.id, status: "received" })),
        source,
      });
      applyResult(res.data);
      toast(res.data.msg);
    } catch (err) {
      handleError(err, navigate, "Unable to update the items.");
      load();
    } finally {
      setSaving(false);
    }
  };

  const revert = async (item) => {
    const result = await Swal.fire({
      title: "Revert to pending?",
      html: `<p style="margin:0">${esc(item.itemName)} was recorded as received${
        item.receivedAt ? ` on ${esc(formatDateTime(item.receivedAt))}` : ""
      }${item.receivedByName ? ` by ${esc(item.receivedByName)}` : ""}. The earlier record stays in the history.</p>`,
      input: "textarea",
      inputLabel: "Reason (required)",
      inputPlaceholder: "e.g. Recorded by mistake / item returned for exchange",
      inputAttributes: { maxlength: "500", "aria-label": "Reason for reverting" },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revert to pending",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
      inputValidator: (value) => (String(value || "").trim().length < 3 ? "Please write a reason (at least 3 characters)." : null),
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
      const res = await axios.post(`/student-package/students/${studentRowId}/status`, {
        updates: [{ distributionId: item.id, status: "pending", reason: result.value.trim() }],
        source,
      });
      applyResult(res.data);
      toast("Reverted to pending");
    } catch (err) {
      handleError(err, navigate, "Unable to revert the item.");
      load();
    } finally {
      setSaving(false);
    }
  };

  const editNotes = async (item) => {
    const result = await Swal.fire({
      title: `Notes — ${esc(item.itemName)}`,
      input: "textarea",
      inputValue: item.notes || "",
      inputPlaceholder: "e.g. Size M given instead of L",
      inputAttributes: { maxlength: "1000", "aria-label": "Notes" },
      showCancelButton: true,
      confirmButtonText: "Save notes",
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
      const res = await axios.patch(`/student-package/distribution/${item.id}/notes`, { notes: result.value });
      toast(res.data.msg);
      await load();
      if (tab === "history") loadHistory();
    } catch (err) {
      handleError(err, navigate, "Unable to save the notes.");
    } finally {
      setSaving(false);
    }
  };

  const initialize = async () => {
    const missing = detail.missingFromBatch;
    const list = missing.map((i) => `<li>${esc(i.itemName)}</li>`).join("");
    const result = await Swal.fire({
      title: items.length ? "Add items from the batch?" : "Create package records?",
      html: `<p style="margin:0 0 8px">These items from ${esc(batchLabel(student.batch))} will be added as <b>pending</b>:</p><ul style="text-align:left;margin:0 auto;display:inline-block">${list}</ul><p style="margin:10px 0 0;font-size:13px;color:#6b7280">Existing records are not changed.</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Add ${missing.length} item(s)`,
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
      const res = await axios.post(`/student-package/students/${studentRowId}/initialize`, { expectedRecords: missing.length });
      applyResult(res.data);
      toast(res.data.msg);
    } catch (err) {
      handleError(err, navigate, "Unable to add the items.");
      load();
    } finally {
      setSaving(false);
    }
  };

  const revokeQr = async () => {
    const result = await Swal.fire({
      title: "Revoke this QR Code?",
      text: "The current QR Code stops working immediately. The student gets a new one the next time they open their portal Profile.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revoke QR Code",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axios.post(`/student-package/students/${studentRowId}/qr/revoke`);
      toast(res.data.msg);
      await load();
    } catch (err) {
      handleError(err, navigate, "Unable to revoke the QR Code.");
    }
  };

  const openAdd = async () => {
    setAdding(true);
    setPick([]);
    setCatalog(null);
    try {
      const res = await axios.get("/student-package/items");
      setCatalog(res.data.items);
    } catch (err) {
      setAdding(false);
      handleError(err, navigate, "Unable to load the package items.");
    }
  };

  const addItems = async () => {
    const chosen = (catalog || []).filter((i) => pick.includes(i.id));
    if (!chosen.length) return;
    if (chosen.length > 1) {
      const result = await Swal.fire({
        title: `Add ${chosen.length} items?`,
        html: `<p style="margin:0 0 8px">These items are added to ${esc(student.name)}'s package as <b>pending</b>:</p><ul style="text-align:left;margin:0 auto;display:inline-block">${chosen
          .map((i) => `<li>${esc(i.itemName)}</li>`)
          .join("")}</ul><p style="margin:10px 0 0;font-size:13px;color:#6b7280">Only this student is changed — the batch configuration stays the same.</p>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: `Add ${chosen.length} items`,
        cancelButtonText: "Cancel",
        confirmButtonColor: BRAND,
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`/student-package/students/${studentRowId}/items`, { itemIds: chosen.map((i) => i.id), source });
      applyResult(res.data);
      setAdding(false);
      setPick([]);
      toast(res.data.msg);
    } catch (err) {
      handleError(err, navigate, "Unable to add the items.");
      // Data mungkin berubah (mis. admin lain baru menambah barang yang sama).
      if (err?.response?.status === 409 || err?.response?.status === 400) {
        load();
        openAdd();
      }
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (item) => {
    const received = item.status === "received";
    const result = await Swal.fire({
      title: `Remove ${esc(item.itemName)}?`,
      html: `<p style="margin:0">Current status: <b>${received ? "Received" : "Pending"}</b>${
        received && item.receivedAt ? ` (${esc(formatDateTime(item.receivedAt))}${item.receivedByName ? ` by ${esc(item.receivedByName)}` : ""})` : ""
      }.</p><p style="margin:8px 0 0;font-size:13px;color:#6b7280">The item is removed from ${esc(
        student.name,
      )}'s active package only. Its history is kept, and the batch configuration and other students are not changed.</p>`,
      input: "textarea",
      inputLabel: "Reason (required)",
      inputPlaceholder: "e.g. Not part of this student's package / added by mistake",
      inputAttributes: { maxlength: "500", "aria-label": "Reason for removing" },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove item",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
      inputValidator: (value) => (String(value || "").trim().length < 3 ? "Please write a reason (at least 3 characters)." : null),
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
      const res = await axios.post(`/student-package/students/${studentRowId}/items/${item.id}/remove`, { reason: result.value.trim(), source });
      applyResult(res.data);
      toast(res.data.msg);
    } catch (err) {
      handleError(err, navigate, "Unable to remove the item.");
      load();
    } finally {
      setSaving(false);
    }
  };

  const renderAddPanel = () => {
    const assignedIds = new Set(items.map((i) => i.packageItemId));
    const active = (catalog || []).filter((i) => i.isActive);
    const inactiveCount = (catalog || []).length - active.length;
    return (
      <div className="spk-add-panel" role="group" aria-label="Add items to this student">
        <p className="spk-add-title">
          Add items to this student
          <span>New items start as Pending. Only this student is changed.</span>
        </p>
        {catalog === null ? (
          <p className="adm-muted-text" style={{ margin: 0 }}>
            Loading items…
          </p>
        ) : active.length === 0 ? (
          <p className="adm-muted-text" style={{ margin: 0 }}>
            There are no active package items.
          </p>
        ) : (
          <ul className="spk-add-list">
            {active.map((i) => {
              const assigned = assignedIds.has(i.id);
              const inBatch = student.batch && i.batches.includes(student.batch);
              return (
                <li key={i.id}>
                  <label className={assigned ? "is-assigned" : pick.includes(i.id) ? "is-on" : ""}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={assigned || pick.includes(i.id)}
                      disabled={assigned || saving}
                      onChange={() => setPick((prev) => (prev.includes(i.id) ? prev.filter((x) => x !== i.id) : [...prev, i.id]))}
                    />
                    <span>{i.itemName}</span>
                    {assigned ? <span className="spk-tag">already assigned</span> : !inBatch ? <span className="spk-tag">not in batch package</span> : null}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {inactiveCount > 0 && <p className="spk-dim" style={{ margin: "8px 0 0" }}>{inactiveCount} inactive item(s) are hidden.</p>}
        <div className="spk-add-actions">
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setAdding(false)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={addItems} disabled={saving || pick.length === 0}>
            Add {pick.length > 0 ? `${pick.length} ` : ""}as pending
          </button>
        </div>
      </div>
    );
  };

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const renderItems = () => {
    if (!items.length) {
      return (
        <div className="spk-callout">
          <i className="bi bi-box-seam" aria-hidden="true"></i>
          <div>
            <p className="spk-callout-title">No package records yet</p>
            <p className="spk-callout-text">
              {detail.missingFromBatch.length
                ? `${batchLabel(student.batch)} has ${detail.missingFromBatch.length} package item(s). Create the records to start recording distribution.`
                : student.batch
                  ? `${batchLabel(student.batch)} has no package items configured yet. Set them up in the Configuration tab.`
                  : "This student has no batch, so no package items apply."}
            </p>
            <div className="d-flex gap-2 flex-wrap">
              {detail.missingFromBatch.length > 0 && (
                <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={initialize} disabled={saving}>
                  <i className="bi bi-plus-lg"></i> Create package records
                </button>
              )}
              {!adding && (
                <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={openAdd} disabled={saving}>
                  Add individual items
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <>
        {detail.missingFromBatch.length > 0 && (
          <div className="spk-callout is-info">
            <i className="bi bi-info-circle" aria-hidden="true"></i>
            <div>
              <p className="spk-callout-text">
                In {batchLabel(student.batch)}'s package but not in this student's: <b>{detail.missingFromBatch.map((i) => i.itemName).join(", ")}</b>.
                It may have been added to the batch later, or removed from this student on purpose.
              </p>
              <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={initialize} disabled={saving}>
                Add to this student
              </button>
            </div>
          </div>
        )}
        {!adding && (
          <div className="spk-items-toolbar">
            <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={openAdd} disabled={saving}>
              <i className="bi bi-plus-lg"></i> Add item
            </button>
          </div>
        )}
        <ul className="spk-items">
          {items.map((item) => {
            const received = item.status === "received";
            return (
              <li key={item.id} className={`spk-item${received ? " is-received" : ""}`}>
                <div className="spk-item-check">
                  {!received && (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selected.includes(item.id)}
                      onChange={() => toggle(item.id)}
                      aria-label={`Select ${item.itemName}`}
                      disabled={saving}
                    />
                  )}
                  {received && <i className="bi bi-check-circle-fill" aria-hidden="true"></i>}
                </div>
                <div className="spk-item-main">
                  <p className="spk-item-name">
                    {item.itemName}
                    {!item.itemActive && <span className="spk-tag">inactive item</span>}
                  </p>
                  <p className="spk-item-meta">
                    {received ? (
                      <>
                        Received {formatDateTime(item.receivedAt)}
                        {item.receivedByName ? ` · by ${item.receivedByName}` : ""}
                      </>
                    ) : (
                      "Not received yet"
                    )}
                  </p>
                  {item.notes && <p className="spk-item-notes">{item.notes}</p>}
                </div>
                <div className="spk-item-actions">
                  <span className={`adm-status ${received ? "adm-status--success" : "adm-status--warning"}`}>
                    {received ? "Received" : "Pending"}
                  </span>
                  <button type="button" className="adm-icon-btn" title="Notes" aria-label={`Notes for ${item.itemName}`} onClick={() => editNotes(item)} disabled={saving}>
                    <i className="bi bi-chat-left-text"></i>
                  </button>
                  {received ? (
                    <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => revert(item)} disabled={saving}>
                      Revert
                    </button>
                  ) : (
                    <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => markReceived([item])} disabled={saving}>
                      Mark received
                    </button>
                  )}
                  <button
                    type="button"
                    className="adm-icon-btn adm-icon-btn--danger"
                    title="Remove from this student"
                    aria-label={`Remove ${item.itemName} from this student`}
                    onClick={() => removeItem(item)}
                    disabled={saving}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </>
    );
  };

  const renderHistory = () => {
    if (history === null) return <p className="adm-muted-text">Loading history…</p>;
    if (!history.length) return <p className="adm-muted-text">No history yet.</p>;
    return (
      <ul className="spk-history">
        {history.map((h) => (
          <li key={h.id}>
            <p className="spk-history-title">
              {ACTION_LABEL[h.action] || h.action}
              {h.itemName ? ` — ${h.itemName}` : ""}
            </p>
            <p className="spk-history-meta">
              {formatDateTime(h.performedAt)} · {h.performedByName || "System"}
              {h.metadata && h.metadata.batch ? ` · Batch ${h.metadata.batch}` : ""}
              {h.metadata && SOURCE_LABEL[h.metadata.source] ? ` · ${SOURCE_LABEL[h.metadata.source]}` : ""}
            </p>
            {h.reason && <p className="spk-history-reason">{h.reason}</p>}
            {h.action === "notes_updated" && h.metadata && (
              <p className="spk-history-reason">{h.metadata.next ? `“${h.metadata.next}”` : "Notes cleared"}</p>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return createPortal(
    <div className="adm-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="adm-modal spk-detail" role="dialog" aria-modal="true" aria-labelledby="spk-detail-title" onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div>
            {fromScan && (
              <p className="spk-verified">
                <i className="bi bi-patch-check-fill" aria-hidden="true"></i> Verified by QR Code
              </p>
            )}
            <h5 id="spk-detail-title" className="adm-card-title" style={{ fontSize: "18px" }}>
              {student ? student.name : "Student package"}
            </h5>
            {student && (
              <p className="adm-card-subtitle">
                {student.studentId} · {student.batch ? batchLabel(student.batch) : "No batch"} · {student.program}
              </p>
            )}
          </div>
          <button type="button" className="adm-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>
            ×
          </button>
        </div>

        <div className="adm-modal-body">
          {loading && !detail && <p className="adm-muted-text">Loading…</p>}
          {loadError && !detail && (
            <div className="spk-callout is-error" role="alert">
              <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
              <div>
                <p className="spk-callout-text">{loadError}</p>
                <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={load}>
                  Try again
                </button>
              </div>
            </div>
          )}
          {detail && (
            <>
              {detail.sharedEmail && (
                <div className="spk-callout is-warning" role="status">
                  <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
                  <p className="spk-callout-text" style={{ margin: 0 }}>
                    This email ({student.email}) is also used by{" "}
                    <b>{detail.sharedEmail.others.map((o) => `${o.name} (${o.studentId})`).join(", ")}</b>.{" "}
                    {detail.sharedEmail.usedByPortal
                      ? "The student portal and QR Code for this email belong to this record."
                      : "The student portal and QR Code for this email belong to the other record, not this one."}{" "}
                    The package below belongs to this record only.
                  </p>
                </div>
              )}
              <div className="spk-facts">
                <div>
                  <span>Collection</span>
                  <b>{student.deliveryMethod || "—"}</b>
                </div>
                <div>
                  <span>T-shirt size</span>
                  <b>{student.tshirtSize ? String(student.tshirtSize).toUpperCase() : "—"}</b>
                </div>
                <div>
                  <span>Received</span>
                  <b>
                    {receivedCount} / {items.length}
                  </b>
                </div>
                <div>
                  <span>QR Code</span>
                  <b>
                    {detail.qr && detail.qr.active ? "Active" : "Not created"}
                    {detail.qr && detail.qr.active && (
                      <button type="button" className="spk-link-danger" onClick={revokeQr}>
                        Revoke
                      </button>
                    )}
                  </b>
                </div>
              </div>
              {items.length > 0 && (
                <div className="adm-progress" style={{ margin: "4px 0 16px" }} aria-hidden="true">
                  <div className="adm-progress-bar" style={{ width: `${(receivedCount / items.length) * 100}%` }} />
                </div>
              )}

              <div className="adm-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={tab === "items"} className={`adm-tab${tab === "items" ? " adm-tab--active" : ""}`} onClick={() => setTab("items")}>
                  Items <span className="adm-tab-count">{items.length}</span>
                </button>
                <button type="button" role="tab" aria-selected={tab === "history"} className={`adm-tab${tab === "history" ? " adm-tab--active" : ""}`} onClick={() => setTab("history")}>
                  History
                </button>
              </div>
              {tab === "items" ? (
                <>
                  {adding && renderAddPanel()}
                  {renderItems()}
                </>
              ) : (
                renderHistory()
              )}
            </>
          )}
        </div>

        <div className="adm-modal-footer spk-detail-footer">
          {fromScan && onScanNext && (
            <button type="button" className="adm-btn adm-btn--ghost me-auto" onClick={onScanNext} disabled={saving}>
              <i className="bi bi-qr-code-scan"></i> Scan next student
            </button>
          )}
          {tab === "items" && pendingItems.length > 0 && (
            <>
              <button
                type="button"
                className="adm-btn adm-btn--secondary"
                onClick={() => markReceived(pendingItems.filter((i) => selected.includes(i.id)))}
                disabled={saving || selected.length === 0}
              >
                Mark selected ({selected.length})
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={() => markReceived(pendingItems)} disabled={saving}>
                <i className="bi bi-check2-all"></i> Mark all pending received
              </button>
            </>
          )}
          {(tab !== "items" || pendingItems.length === 0) && (
            <button type="button" className="adm-btn adm-btn--secondary" onClick={onClose} disabled={saving}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default StudentPackageDetail;
